import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

interface DebtReminder {
  id: string;
  processing_date: string;
  client_id: string;
  client_name: string;
  most_overdue_days: number;
  sales_condition_name: string;
  reminder_type: string;
  status: string;
  failure_reason: string | null;
  processed_at: string | null;
  created_at: string;
}

interface ClientReminderSummary {
  client_id: string;
  client_name: string;
  reminder_count: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  last_processed_at: string | null;
}

interface ReminderStats {
  total_processed: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
}

export const useDebtReminders = (
  selectedDate: string,
  page: number = 1,
  pageSize: number = 10
) => {
  const { data: remindersData, isLoading } = useQuery({
    queryKey: ["debt-reminders", selectedDate, page, pageSize],
    queryFn: async (): Promise<{ data: DebtReminder[]; total: number }> => {
      // Get all reminder data and join with clients to check is_test
      const { data: allData, error: allError } = await supabase
        .from("daily_reminder_progress")
        .select(
          `
          *,
          clients!inner(is_test)
        `
        )
        .eq("processing_date", selectedDate)
        .eq("clients.is_test", false) // Filter out test clients directly in the query
        .order("created_at", { ascending: false });

      if (allError) throw allError;

      // Apply pagination to filtered results
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedData = (allData || []).slice(start, end);

      return { data: paginatedData, total: (allData || []).length };
    },
    enabled: !!selectedDate,
  });

  const reminders = remindersData?.data || [];
  const totalCount = remindersData?.total || 0;

  const { data: stats } = useQuery({
    queryKey: ["debt-reminders-stats", selectedDate],
    queryFn: async (): Promise<ReminderStats> => {
      const { data, error } = await supabase
        .from("daily_reminder_progress")
        .select(
          `
          status,
          clients!inner(is_test)
        `
        )
        .eq("processing_date", selectedDate)
        .eq("clients.is_test", false); // Filter out test clients

      if (error) throw error;

      const stats = (data || []).reduce(
        (acc, item) => {
          acc.total_processed++;
          if (item.status === "sent") acc.sent_count++;
          else if (item.status === "failed") acc.failed_count++;
          else if (item.status === "pending") acc.pending_count++;
          return acc;
        },
        {
          total_processed: 0,
          sent_count: 0,
          failed_count: 0,
          pending_count: 0,
        }
      );

      return stats;
    },
    enabled: !!selectedDate,
  });

  return {
    reminders,
    totalCount,
    isLoading,
    stats: stats || {
      total_processed: 0,
      sent_count: 0,
      failed_count: 0,
      pending_count: 0,
    },
  };
};

export const useClientReminders = (
  selectedDate: string,
  searchQuery: string = "",
  page: number = 1,
  pageSize: number = 10
) => {
  const { data: clientData, isLoading } = useQuery({
    queryKey: ["client-reminders", selectedDate, searchQuery, page, pageSize],
    queryFn: async (): Promise<{
      data: ClientReminderSummary[];
      total: number;
    }> => {
      // Get data with clients join to filter test clients
      let baseQuery = supabase
        .from("daily_reminder_progress")
        .select(
          `
          client_id, 
          client_name, 
          status, 
          processed_at,
          clients!inner(is_test)
        `
        )
        .eq("processing_date", selectedDate)
        .eq("clients.is_test", false); // Filter out test clients

      if (searchQuery) {
        baseQuery = baseQuery.ilike("client_name", `%${searchQuery}%`);
      }

      const { data, error } = await baseQuery.order("client_name");

      if (error) throw error;

      // Group by client
      const clientMap = new Map<string, ClientReminderSummary>();

      (data || []).forEach((item) => {
        const existing = clientMap.get(item.client_id);
        if (existing) {
          existing.reminder_count++;
          if (item.status === "sent") existing.sent_count++;
          else if (item.status === "failed") existing.failed_count++;
          else if (item.status === "pending") existing.pending_count++;

          if (
            !existing.last_processed_at ||
            (item.processed_at &&
              item.processed_at > existing.last_processed_at)
          ) {
            existing.last_processed_at = item.processed_at;
          }
        } else {
          clientMap.set(item.client_id, {
            client_id: item.client_id,
            client_name: item.client_name,
            reminder_count: 1,
            sent_count: item.status === "sent" ? 1 : 0,
            failed_count: item.status === "failed" ? 1 : 0,
            pending_count: item.status === "pending" ? 1 : 0,
            last_processed_at: item.processed_at,
          });
        }
      });

      const allClients = Array.from(clientMap.values());

      // Apply pagination to grouped results
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedClients = allClients.slice(start, end);

      // Calculate overall stats
      const overallStats = {
        total_processed: allClients.reduce(
          (sum, c) => sum + c.reminder_count,
          0
        ),
        sent_count: allClients.reduce((sum, c) => sum + c.sent_count, 0),
        failed_count: allClients.reduce((sum, c) => sum + c.failed_count, 0),
        pending_count: allClients.reduce((sum, c) => sum + c.pending_count, 0),
      };

      return {
        data: paginatedClients,
        total: allClients.length,
        stats: overallStats,
      };
    },
    enabled: !!selectedDate,
  });

  return {
    clients: clientData?.data || [],
    totalCount: clientData?.total || 0,
    stats: clientData?.stats || {
      total_processed: 0,
      sent_count: 0,
      failed_count: 0,
      pending_count: 0,
    },
    isLoading,
  };
};

export const useClientReminderDetails = (
  clientId: string,
  selectedDate: string
) => {
  return useQuery({
    queryKey: ["client-reminder-details", clientId, selectedDate],
    queryFn: async (): Promise<DebtReminder[]> => {
      const { data, error } = await supabase
        .from("daily_reminder_progress")
        .select("*")
        .eq("client_id", clientId)
        .eq("processing_date", selectedDate)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!clientId && !!selectedDate,
  });
};

interface ClientEligibleForReminder {
  client_id: string;
  client_name: string;
  client_email: string;
  overdue_amount: number;
  overdue_days: number;
  total_overdue_sales_requests: number;
  reminder_type: "pre_expiration" | "early_overdue" | "final_notice";
  most_urgent_factura_number: string | null;
}

export const useClientsEligibleForReminders = (selectedDate: string) => {
  return useQuery({
    queryKey: ["clients-eligible-for-reminders", selectedDate],
    queryFn: async (): Promise<ClientEligibleForReminder[]> => {
      // Call the get_debt_reminder_candidates function to get clients eligible for reminders
      const { data: eligibleClients, error } = await supabase.rpc(
        "get_debt_reminder_candidates",
        {
          cutoff_date: selectedDate,
        }
      );

      if (error) {
        console.error("Error fetching clients eligible for reminders:", error);
        throw error;
      }

      // If no eligible clients, return empty array
      if (!eligibleClients || eligibleClients.length === 0) {
        return [];
      }

      // Get client IDs to check is_test field
      const clientIds = eligibleClients.map((c) => c.client_id);

      // Fetch is_test field for all clients
      const { data: clientTestInfo, error: testError } = await supabase
        .from("clients")
        .select("id, is_test")
        .in("id", clientIds);

      if (testError) {
        console.error("Error fetching client test info:", testError);
        throw testError;
      }

      // Create a map of client test status
      const testMap = new Map();
      (clientTestInfo || []).forEach((client) => {
        testMap.set(client.id, client.is_test);
      });

      // Filter out test clients using is_test field
      const filteredClients = eligibleClients.filter((client) => {
        const isTest = testMap.get(client.client_id);
        return !isTest; // Only include clients where is_test is false or null
      });

      // Get debt amounts and counts for each client (use filtered clients)
      const filteredClientIds = filteredClients.map((c) => c.client_id);

      // Get debt data from sales requests
      const { data: debtData, error: debtError } = await supabase.rpc(
        "get_sales_requests_debt_at_cutoff",
        {
          cutoff_date: selectedDate,
        }
      );

      if (debtError) {
        console.warn("Error fetching debt data:", debtError);
      }

      // Create a map of client debt info with most urgent factura
      const debtMap = new Map();
      (debtData || []).forEach((debt) => {
        if (filteredClientIds.includes(debt.client_id)) {
          if (!debtMap.has(debt.client_id)) {
            debtMap.set(debt.client_id, {
              total_amount: 0,
              total_requests: 0,
              most_urgent_factura: null,
              most_urgent_days: null,
            });
          }
          const existing = debtMap.get(debt.client_id);
          existing.total_amount += debt.debt_amount_at_cutoff || 0;
          existing.total_requests += 1;

          // Track the most urgent (most overdue or closest to expiring) factura
          const currentDays = debt.days_to_due_at_cutoff;
          if (
            existing.most_urgent_days === null ||
            (currentDays < 0 && existing.most_urgent_days >= 0) || // Overdue takes priority over not-yet-due
            (currentDays < 0 &&
              existing.most_urgent_days < 0 &&
              currentDays < existing.most_urgent_days) || // Most overdue among overdue
            (currentDays >= 0 &&
              existing.most_urgent_days >= 0 &&
              currentDays < existing.most_urgent_days)
          ) {
            // Closest to expiring among not-yet-due
            existing.most_urgent_days = currentDays;
            existing.most_urgent_factura = debt.factura_number;
          }
        }
      });

      // Transform the data to match our interface (use filtered clients)
      const transformedClients: ClientEligibleForReminder[] =
        filteredClients.map((client) => {
          const debtInfo = debtMap.get(client.client_id) || {
            total_amount: 0,
            total_requests: 0,
            most_urgent_factura: null,
          };

          return {
            client_id: client.client_id,
            client_name: client.client_name,
            client_email: client.client_email,
            overdue_amount: debtInfo.total_amount,
            overdue_days: client.most_overdue_days || 0,
            total_overdue_sales_requests: debtInfo.total_requests,
            reminder_type: client.reminder_type as
              | "pre_expiration"
              | "early_overdue"
              | "final_notice",
            most_urgent_factura_number: debtInfo.most_urgent_factura,
          };
        });

      return transformedClients;
    },
    enabled: !!selectedDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
